import React from 'react';

import { faPause, faPlay } from '@fortawesome/pro-light-svg-icons';
import { useGetCronJobs } from 'queries';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { CronJob, CronJobPayload, PartialCronJobPayload } from './types';

import { CRON_JOBS } from 'RestAPIs/endpoints';
import { useDeleteById } from 'RestAPIs/useDeleteById';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

import { CronJobsCRUDForm } from './CronJobsCRUDForm';
import { useChangeRunStatus } from './hooks/useChangeRunStatus';
import useColumnDefinitions from './hooks/useColumnDefinitions';

interface Props {
  schema: Yup.AnyObjectSchema;
  convertData?: (data: CronJobPayload) => CronJobPayload;
  defaultFormValues?: PartialCronJobPayload;
}

const CronJobsCRUD: React.FC<Props> = ({
  schema,
  convertData,
  children,
  defaultFormValues
}) => {
  const { t } = useTranslation();
  const { data, isFetching } = useGetCronJobs();
  const columnDefinitions = useColumnDefinitions();
  const { mutate: handleDeleteClick } = useDeleteById(`${CRON_JOBS}/dzz`, [
    'cron-jobs',
    'list'
  ]);

  const { mutate: handlePause } = useChangeRunStatus();

  const handlePauseClick = (id: number | string, row?: CronJob) => {
    handlePause({ id, row });
  };

  return (
    <TableCRUDLayout
      FormComponent={({ handleClose }) => (
        <CronJobsCRUDForm
          onClose={handleClose}
          schema={schema}
          convertData={convertData}
          defaultValues={defaultFormValues}
        >
          {children}
        </CronJobsCRUDForm>
      )}
      loading={isFetching}
      columns={columnDefinitions}
      rows={data}
      getRowId={(row) => row.jobName}
      editable={false}
      customActions={(id, row) => [
        {
          tooltipTitle: t(
            `cronJobs.grid.${row?.state === 'PAUSED' ? 'start' : 'pause'}`
          ),
          icon: row?.state === 'PAUSED' ? faPlay : faPause,
          onClick: handlePauseClick
        }
      ]}
      onDeleteClick={handleDeleteClick}
      confirmationTitle={t('deleteDialog.title')}
      confirmationBody={t('deleteDialog.body')}
    />
  );
};

export default CronJobsCRUD;
